import type { z } from 'zod';
import { API_PREFIX, ErrorEnvelope, routes, type ErrorCode } from '@bs/contracts';
import { typed } from './typedRoutes.js';

type Typed = typeof typed;
export type RouteName = keyof Typed;
type BodyOf<K extends RouteName> = Typed[K]['body'] extends z.ZodTypeAny ? z.input<Typed[K]['body']> : undefined;
type QueryOf<K extends RouteName> = Typed[K]['query'] extends z.ZodTypeAny ? Partial<z.input<Typed[K]['query']>> : undefined;
export type ResponseOf<K extends RouteName> = z.output<Typed[K]['response']>;

/** Client-side failure codes in addition to the server's ErrorCode set. */
export type ClientErrorCode = ErrorCode | 'NETWORK' | 'BAD_RESPONSE';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ClientErrorCode,
    message: string,
    readonly law?: string,
    readonly details?: { path: string; message: string }[],
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface CallOptions<K extends RouteName> {
  params?: Record<string, string>;
  query?: QueryOf<K>;
  body?: BodyOf<K>;
  signal?: AbortSignal;
}

export interface ApiClientConfig {
  baseUrl: string;
  fetch?: typeof fetch;
  /** Called on 401 so the app can return to the login screen. */
  onUnauthenticated?: () => void;
}

const MUTATING = new Set(['POST', 'PATCH', 'PUT', 'DELETE']);

export function buildPath(path: string, params: Record<string, string> = {}): string {
  return path.replace(/:([A-Za-z]+)/g, (_m, key: string) => {
    const v = params[key];
    if (!v) throw new ApiError(0, 'BAD_REQUEST', `missing path parameter "${key}"`);
    return encodeURIComponent(v);
  });
}

export function createApiClient(cfg: ApiClientConfig) {
  const doFetch = cfg.fetch ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
  let csrfToken: string | null = null;

  async function call<K extends RouteName>(name: K, opts: CallOptions<K> = {}): Promise<ResponseOf<K>> {
    const route = routes[name];
    const schema = typed[name] as { body?: z.ZodTypeAny; response: z.ZodTypeAny };
    let url = cfg.baseUrl.replace(/\/$/, '') + API_PREFIX + buildPath(route.path, opts.params);
    if (opts.query) {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(opts.query as Record<string, unknown>)) {
        if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
      }
      const s = qs.toString();
      if (s) url += `?${s}`;
    }
    const headers: Record<string, string> = { Accept: 'application/json' };
    let body: string | undefined;
    if (schema.body) {
      const parsed = schema.body.safeParse(opts.body ?? {});
      if (!parsed.success) {
        throw new ApiError(0, 'VALIDATION_FAILED', 'Please check the highlighted fields.', undefined,
          parsed.error.issues.map((i) => ({ path: i.path.join('.'), message: i.message })));
      }
      body = JSON.stringify(parsed.data);
      headers['Content-Type'] = 'application/json';
    }
    if (MUTATING.has(route.method) && csrfToken) headers['X-CSRF-Token'] = csrfToken;

    let res: Response;
    try {
      res = await doFetch(url, { method: route.method, headers, body, credentials: 'include', signal: opts.signal });
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') throw e;
      throw new ApiError(0, 'NETWORK', 'Cannot reach the server. Check your connection and try again.');
    }

    if (!res.ok) throw await toApiError(res, cfg.onUnauthenticated);

    if (res.status === 204) return schema.response.parse(undefined) as ResponseOf<K>;
    let json: unknown;
    try {
      json = await res.json();
    } catch {
      throw new ApiError(res.status, 'BAD_RESPONSE', 'The server sent a response that is not JSON.');
    }
    const parsed = schema.response.safeParse(json);
    if (!parsed.success) {
      throw new ApiError(res.status, 'BAD_RESPONSE', `The server response for ${String(name)} did not match the contract.`);
    }
    if (name === 'login') csrfToken = (parsed.data as { csrfToken: string }).csrfToken;
    if (name === 'logout') csrfToken = null;
    return parsed.data as ResponseOf<K>;
  }

  return {
    call,
    setCsrfToken(t: string | null) { csrfToken = t; },
    getCsrfToken() { return csrfToken; },
  };
}

async function toApiError(res: Response, onUnauth?: () => void): Promise<ApiError> {
  if (res.status === 401) onUnauth?.();
  let raw: unknown = null;
  const text = await res.text();
  if (text) {
    try { raw = JSON.parse(text); } catch { raw = null; }
  }
  const env = ErrorEnvelope.safeParse(raw);
  if (env.success) {
    const e = env.data;
    return new ApiError(res.status, e.code, e.message, e.law, e.details, e.requestId);
  }
  const fallback: ClientErrorCode =
    res.status === 401 ? 'UNAUTHENTICATED' : res.status === 403 ? 'FORBIDDEN' : res.status === 404 ? 'NOT_FOUND'
    : res.status === 409 ? 'CONFLICT' : res.status === 429 ? 'RATE_LIMITED' : res.status >= 500 ? 'INTERNAL' : 'BAD_REQUEST';
  return new ApiError(res.status, fallback, `Request failed (HTTP ${res.status}).`);
}

/**
 * Plain human message for a toast or inline error. The law code is NOT prefixed (users are
 * non-technical); read it from `errorLaw(e)` and show it in a small secondary line / tooltip.
 */
export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.code === 'VERSION_CONFLICT') return 'Someone else changed this. Reload and try again.';
    return e.message;
  }
  if (e instanceof Error) return e.message;
  return 'Something went wrong.';
}

/** The selling-rule code the server cited, if any (e.g. L117). */
export function errorLaw(e: unknown): string | undefined {
  return e instanceof ApiError ? e.law : undefined;
}

export type ApiClient = ReturnType<typeof createApiClient>;
